import React, { useEffect, useState } from "react";
import { Empty } from "./OverviewTab";
import { useT, useI18n } from "../../lib/i18n";
import { api, formatApiError } from "../../lib/api";
import { CheckCircle, DownloadSimple, FloppyDisk, WarningCircle } from "@phosphor-icons/react";

const STATUSES = ["applicable", "not_applicable", "pending"];

export default function GsprTab({ project, version, deviceId, onChange }) {
  const t = useT();
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [state, setState] = useState({});
  const [standards, setStandards] = useState([]);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [showDownloadWarning, setShowDownloadWarning] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reference/gspr");
        setItems(data.items || []);
        if (version?.classification?.suggested_class) {
          const { data: std } = await api.get(`/reference/standards/${version.classification.suggested_class}`);
          setStandards(std.standards || []);
        }
        setState(version?.gspr_state || {});
        setError("");
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail));
      }
    })();
  }, [version?.version, version?.classification?.suggested_class, version?.gspr_state]);

  if (!version) return <Empty msg={t("ov.no_analysis")} />;

  function setField(id, key, value) {
    setSaved(false);
    setError("");
    setShowDownloadWarning(false);
    setState({ ...state, [id]: { ...(state[id] || {}), [key]: value } });
  }

  async function save() {
    setSaving(true);
    setError("");
    try {
      await api.put(
        `/projects/${project.id}/versions/${version.version}/gspr`,
        { items: state },
        { params: deviceId ? { device_id: deviceId } : {} },
      );
      setSaved(true);
      onChange?.();
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
      return false;
    } finally { setSaving(false); }
  }

  const incompleteItems = items.filter((it) => {
    const entry = state[it.id] || {};
    return !["applicable", "not_applicable"].includes(entry.status) || !String(entry.evidence || "").trim();
  });

  function focusFirstIncomplete() {
    setShowDownloadWarning(false);
    const first = incompleteItems[0];
    if (!first) return;
    window.setTimeout(() => {
      const input = document.getElementById(`gspr-ev-${first.id}`);
      input?.scrollIntoView({ behavior: "smooth", block: "center" });
      input?.focus();
    }, 0);
  }

  async function downloadReport(allowDraft = false) {
    if (!items.length) {
      setError(t("gspr.report_not_ready"));
      return;
    }
    if (incompleteItems.length && !allowDraft) {
      setShowDownloadWarning(true);
      return;
    }
    setDownloading(true);
    setError("");
    try {
      const savedOk = await save();
      if (!savedOk) return;
      const { data } = await api.get(
        `/projects/${project.id}/versions/${version.version}/gspr/pdf`,
        { params: { lang, ...(deviceId ? { device_id: deviceId } : {}) }, responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `RA-GSPR_${project.id.slice(0, 8)}_v${version.version}.pdf`;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      setShowDownloadWarning(false);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setDownloading(false);
    }
  }

  const applicable = items.filter(it => state[it.id]?.status === "applicable").length;
  const notApp = items.filter(it => state[it.id]?.status === "not_applicable").length;
  const pending = items.length - applicable - notApp;
  const coverage = items.length ? Math.round(((applicable + notApp) / items.length) * 100) : 0;

  return (
    <div className="space-y-4" data-testid="gspr-tab">
      <div className="grid-card p-5 flex items-center justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{t("gspr.title")}</div>
          <p className="text-sm text-zinc-600 mt-1">{t("gspr.sub")}</p>
        </div>
        <div className="text-right">
          <div className="text-xs font-mono uppercase text-zinc-500">{t("gspr.coverage")}</div>
          <div className="font-mono text-3xl font-extrabold text-blue-700 mt-1">{coverage}%</div>
          <div className="text-[10px] font-mono text-zinc-500">{applicable} ✓ · {notApp} ✗ · {pending} —</div>
        </div>
      </div>

      {standards.length > 0 && (
        <div className="grid-card p-5">
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">{t("coach.standards")}</div>
          <div className="flex flex-wrap gap-2">
            {standards.map((st) => <span key={st} className="text-xs font-mono bg-blue-50 text-blue-800 border border-blue-200 px-2.5 py-1 rounded-sm">{st}</span>)}
          </div>
        </div>
      )}

      <div className="grid-card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left font-mono text-xs uppercase tracking-wider">
              <th className="px-3 py-2 w-12">#</th>
              <th className="px-3 py-2">{t("gspr.title")}</th>
              <th className="px-3 py-2 w-44">Status</th>
              <th className="px-3 py-2 w-[30%]">{t("gspr.evidence")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => {
              const cur = state[it.id] || {};
              return (
                <tr key={it.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-500">{it.id}</td>
                  <td className="px-3 py-2">
                    <div>{lang === "es" ? it.es : it.en}</div>
                    <div className="font-mono text-[10px] text-zinc-400 mt-0.5">{it.ref}</div>
                  </td>
                  <td className="px-3 py-2">
                    <select value={cur.status || "pending"} onChange={(e) => setField(it.id, "status", e.target.value)}
                      data-testid={`gspr-status-${it.id}`}
                      className={`border px-2 py-1 rounded-sm text-xs font-mono w-full bg-white focus:outline-none focus:border-blue-600
                        ${cur.status === "applicable" ? "border-emerald-300 text-emerald-700" :
                          cur.status === "not_applicable" ? "border-zinc-300 text-zinc-500" :
                          "border-amber-300 text-amber-700"}`}>
                      {STATUSES.map(s => <option key={s} value={s}>{t(`gspr.status.${s}`)}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input id={`gspr-ev-${it.id}`} value={cur.evidence || ""} onChange={(e) => setField(it.id, "evidence", e.target.value)}
                      data-testid={`gspr-ev-${it.id}`}
                      className="w-full border border-zinc-200 px-2 py-1 rounded-sm text-xs focus:border-blue-600 focus:outline-none"
                      placeholder="—" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="grid-card p-3 text-xs text-zinc-600" data-testid="gspr-report-scope">
        {t("gspr.report_scope")}
      </div>

      {showDownloadWarning && incompleteItems.length > 0 && (
        <div className="grid-card border-l-4 border-l-amber-500 p-4 space-y-3" role="alert" data-testid="gspr-incomplete-warning">
          <div className="flex items-start gap-2 text-sm text-amber-800">
            <WarningCircle size={17} weight="fill" className="shrink-0 mt-0.5" />
            <span>{t("gspr.incomplete_warning").replace("{count}", String(incompleteItems.length))}</span>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <button onClick={focusFirstIncomplete} className="border border-zinc-300 px-3 py-2 rounded-sm text-xs font-semibold hover:bg-zinc-50" data-testid="gspr-complete-first">
              {t("gspr.complete_first")}
            </button>
            <button onClick={() => downloadReport(true)} disabled={downloading || saving} className="btn-primary px-3 py-2 text-xs disabled:opacity-50" data-testid="gspr-download-draft">
              {downloading ? "…" : t("gspr.download_draft")}
            </button>
          </div>
        </div>
      )}

      {error && <div className="border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm" role="alert" data-testid="gspr-error">{error}</div>}

      <div className="flex flex-wrap items-center justify-end gap-3">
        {saved && <span className="text-xs font-mono text-emerald-700 inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> {t("gspr.saved")}</span>}
        <button onClick={save} disabled={saving || downloading} data-testid="gspr-save-btn" className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm">
          <FloppyDisk size={14} weight="bold" /> {saving ? "…" : t("gspr.save")}
        </button>
        <button onClick={() => downloadReport(false)} disabled={saving || downloading || !items.length} data-testid="gspr-download-btn" className="inline-flex items-center gap-2 border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-800 px-4 py-2 rounded-sm text-sm font-semibold disabled:opacity-50">
          <DownloadSimple size={14} weight="bold" /> {downloading ? t("gspr.downloading") : t("gspr.download_report")}
        </button>
      </div>
    </div>
  );
}
